import { Account, Flag, Service, Variant } from '@acies/domain';
import { IFeatureFlagsJSON, IFlagJSON, IVariantJSON } from '@acies/schema';
import ParseError from '@shared/errors/parse-error';
import { map } from 'rxjs';
import { ZodType } from 'zod';

/** Safely validates the JSON and returns a ParseError if it fails, otherwise the correct schema data. */
export function parse<T>(schema: ZodType<T>) {
	return map((data: unknown) => {
		const parsed = schema.safeParse(data);
		if (!parsed.success) {
			return new ParseError(parsed.error.message);
		}
		return parsed.data;
	});
}

/**
 * Create Services instances from the featureflags json response.
 *
 * It groups by services so it's easier to create UI lists.
 */
export function transformFeatureFlags(account: Account) {
	const servicesMap: Map<string, Service> = new Map();
	return map((data: IFeatureFlagsJSON) => {
		for (const flag of data.result) {
			let service = servicesMap.get(flag.service);
			if (!service) {
				service = new Service({ flags: [], name: flag.service });
				servicesMap.set(service.name, service);
			}
			service.flags.push(flagTransform(flag, account));
		}
		return Array.from(servicesMap.values());
	});
}

/** Transforms flag json to Flag instances with variants. */
function flagTransform({ createdAt, description, enabled, expiresAt, id, service, variants: vars }: IFlagJSON, account: Account): Flag {
	const variants = vars.map((json) => variantTransform(json, account));
	return new Flag({
		createdAt: new Date(createdAt),
		description,
		enabled,
		expiresAt: new Date(expiresAt),
		name: id,
		service,
		variants,
	});
}

/** Transforms variant JSON to an instance of Variant. */
function variantTransform({ enabled }: IVariantJSON, account: Account): Variant {
	return new Variant({
		account,
		enabled,
	});
}
